import { Color } from '../types/Color';
import { Merkmal } from '../types/Merkmal';
import { SelectedMerkmal } from '../types/Merkmal';

interface AutoSelectResult {
    newSelections: SelectedMerkmal[];
    shouldUpdate: boolean;
}

export const autoSelectColorMerkmale = (
    primaryColorHex: string | undefined,
    secondaryColorHex: string | undefined,
    colorObjects: Color[],
    availableMerkmale: Merkmal[],
    currentSelections: SelectedMerkmal[],
    userDeselectedColors: Set<string>
): AutoSelectResult => {
    // Get color names from hex values
    const primaryColorName = primaryColorHex 
        ? colorObjects.find(c => c.color.toUpperCase() === primaryColorHex.toUpperCase())?.name.toLowerCase()
        : undefined;
    const secondaryColorName = secondaryColorHex 
        ? colorObjects.find(c => c.color.toUpperCase() === secondaryColorHex.toUpperCase())?.name.toLowerCase()
        : undefined;

    // Find "farbe" merkmal
    const farbeMerkmal = availableMerkmale.find(m => 
        m.name.toLowerCase() === 'farbe' || m.name.toLowerCase() === 'color'
    );

    if (!farbeMerkmal) {
        return { newSelections: currentSelections, shouldUpdate: false };
    }

    const selectedColorNames: string[] = [];
    if (primaryColorName && !userDeselectedColors.has(primaryColorName)) {
        selectedColorNames.push(primaryColorName);
    }
    if (secondaryColorName && !userDeselectedColors.has(secondaryColorName)) {
        selectedColorNames.push(secondaryColorName);
    }

    if (selectedColorNames.length === 0) {
        // Remove all farbe merkmal selections if no colors selected
        const otherMerkmale = currentSelections.filter(m => m.kMerkmal !== farbeMerkmal.id);
        return { newSelections: otherMerkmale, shouldUpdate: true };
    }

    const matchingValues = farbeMerkmal.values.filter(wert => 
        selectedColorNames.some(colorName => wert.name.toLowerCase() === colorName)
    );

    if (matchingValues.length === 0) {
        // Remove all farbe merkmal selections if no matches
        const otherMerkmale = currentSelections.filter(m => m.kMerkmal !== farbeMerkmal.id);
        return { newSelections: otherMerkmale, shouldUpdate: true };
    }

    // Build new selections: keep other merkmale, replace farbe selections
    const otherMerkmale = currentSelections.filter(m => m.kMerkmal !== farbeMerkmal.id);
    const colorSelections: SelectedMerkmal[] = matchingValues.map(wert => ({
        kMerkmal: farbeMerkmal.id,
        kMerkmalWert: wert.id
    }));

    return { 
        newSelections: [...otherMerkmale, ...colorSelections], 
        shouldUpdate: true 
    };
};


export const getColorNamesFromHex = (
    primaryColorHex: string | undefined,
    secondaryColorHex: string | undefined,
    colorObjects: Color[]
): { primaryColorName?: string; secondaryColorName?: string } => {
    const primaryColorName = primaryColorHex 
        ? colorObjects.find(c => c.color.toUpperCase() === primaryColorHex.toUpperCase())?.name.toLowerCase()
        : undefined;
    const secondaryColorName = secondaryColorHex 
        ? colorObjects.find(c => c.color.toUpperCase() === secondaryColorHex.toUpperCase())?.name.toLowerCase()
        : undefined;

    return { primaryColorName, secondaryColorName };
};
